<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content">
        <section class="intro">
          <p>
            Sharing 360 photos on social media reaches a significantly larger
            audience than website-only publishing, but each platform handles 360
            content differently. Facebook renders 360 photos interactively;
            Instagram does not support native 360 viewing; YouTube supports 360
            video but not still 360 photos. Understanding which platforms
            support which formats — and how to maximise reach on each —
            determines whether your 360 content reaches thousands of viewers or
            just a handful.
          </p>
        </section>

        <section>
          <h2>Platform-by-Platform 360 Photo Support</h2>

          <h3>Facebook: Full Native 360 Support</h3>
          <p>
            Facebook is the best platform for 360 photo sharing. When you upload
            an equirectangular JPEG with proper XMP photo sphere metadata,
            Facebook automatically detects the 360 format and renders it as an
            interactive spherical viewer. Users can pan, tilt, and zoom directly
            in their newsfeed on both desktop and mobile.
          </p>
          <p>
            <strong>How to post 360 on Facebook:</strong> Upload the
            equirectangular source image file directly as a photo post (not via
            a link). Do not share via URL — Facebook only activates the 360
            viewer for directly uploaded images. The file must contain the
            Google Photo Sphere XMP metadata. Files exported from Travvir,
            Insta360, Ricoh Theta, and Google Street View app all contain this
            metadata automatically.
          </p>
          <p>
            <strong>Engagement impact:</strong> 360 photos on Facebook receive
            on average <strong>3× more interactions</strong> than standard
            photos and <strong>4× more shares</strong> — making them
            significantly more effective for organic reach than conventional
            photography.
          </p>

          <h3>Instagram: No Native 360 Support</h3>
          <p>
            Instagram does not render 360 photos as interactive content. If you
            upload an equirectangular 360 image, it appears as a flat, heavily
            distorted rectangle — the characteristic warped appearance of an
            unmapped equirectangular projection.
          </p>
          <p>
            <strong>Best approach for Instagram:</strong> Export a standard flat
            "preview" crop from your 360 image — a wide-angle or dual-pane style
            extract that shows the most interesting view. Include text in the
            caption directing followers to a link (in bio or Stories link
            sticker) where they can experience the full interactive tour.
            Alternatively, record a short screen capture video panning through
            the 360 tour and post it as a Reel — this shows the interactive
            quality of the content in Instagram's video format.
          </p>

          <h3>YouTube: 360 Video Only (Not Photos)</h3>
          <p>
            YouTube supports 360 video with full interactive viewing (users can
            pan and tilt during playback), but does not support 360 still
            photos. To share 360 content on YouTube, create a 360 video — either
            by recording a timelapse or slow-panning video in 360 mode, or by
            using video slideshow software to create a rotating video from your
            360 still images.
          </p>

          <h3>Twitter/X: No 360 Support</h3>
          <p>
            Twitter/X renders uploaded images as standard flat images regardless
            of metadata. Share a compelling crop or thumbnail with a link to the
            interactive version on your website or Travvir tour URL.
          </p>

          <h3>LinkedIn: No Native 360 Support</h3>
          <p>
            LinkedIn does not render 360 photos interactively. Use the same
            approach as Instagram: share a standard crop with a link to the full
            experience. LinkedIn is a useful distribution channel for B2B
            applications of virtual tours — sharing with property industry
            professionals, hospitality sector contacts, or potential B2B
            clients.
          </p>

          <h3>Google Business Profile: Native 360 Integration</h3>
          <p>
            Google Business Profile is one of the best 360 photo channels for
            local businesses. Upload directly via the Google Maps app or Street
            View app — photos appear in the "See Inside" section of your
            Business Profile and can influence local search rankings. For a
            complete guide on this, see our article on
            <a href="/blog/en/google-business-virtual-tour"
              >Google Business virtual tours</a
            >.
          </p>
        </section>

        <section>
          <h2>Creating Platform-Optimised Exports</h2>

          <h3>Facebook Export Settings</h3>
          <p>
            For Facebook upload: export as equirectangular JPEG at
            <strong>6000 × 3000 pixels minimum</strong> (higher is better but
            Facebook compresses on ingestion). Maximum file size 30 MB. Ensure
            XMP photo sphere metadata is embedded — Travvir exports include this
            automatically.
          </p>

          <h3>Instagram Crops from 360 Images</h3>
          <p>
            To extract a compelling flat crop from your 360 image: open the 360
            file in a viewer, navigate to the best angle, and take a screenshot
            at the highest resolution your device supports. Alternatively, use
            Photoshop or the free app Theta+ (iOS/Android) to extract flat crops
            at custom resolutions. Instagram's optimal aspect ratio is 4:5
            (portrait) or 1:1 (square).
          </p>

          <h3>Thumbnail Images for Link Shares</h3>
          <p>
            When sharing a virtual tour link on any platform (LinkedIn, Twitter,
            email), the preview image (Open Graph image) is what drives
            click-through rate. Ensure your tour platform has a well-set
            thumbnail — in Travvir, set the tour thumbnail in the tour settings
            to the most compelling single view of the space. A compelling
            thumbnail increases link click-through rate by
            <strong>35–55%</strong> compared to an auto-generated preview.
          </p>
        </section>

        <section>
          <h2>Social Media Content Strategy for 360 Tours</h2>

          <h3>Facebook Posting Best Practices</h3>
          <p>
            Post 360 photos as standalone posts (not albums) for maximum
            engagement — album posts do not activate the interactive 360 viewer.
            Write captions that explicitly tell viewers they can drag to look
            around: "Drag to explore our new [space name] ↔️" performs
            significantly better than captions that don't mention the
            interactivity. Post at peak engagement times: Tuesday–Thursday
            10am–2pm typically produces highest reach for business pages.
          </p>

          <h3>Cross-Platform Distribution Workflow</h3>
          <p>
            A single 360 photo shoot produces content for a cross-platform
            campaign: (1) Upload the full equirectangular image directly to
            Facebook for interactive engagement. (2) Export flat crops for
            Instagram feed posts with link-in-bio to the full tour. (3) Create a
            15–30 second screen-capture video for Instagram Reels and TikTok.
            (4) Share the tour URL on LinkedIn with context about your business
            or property. (5) Add to Google Business Profile for local search
            visibility.
          </p>
          <p>
            This multi-channel approach from a single photo session maximises
            the return on the time invested in creating the 360 content.
          </p>
        </section>

        <section>
          <h2>Conclusion: Match Format to Platform</h2>
          <p>
            360 photo sharing is not a one-size-fits-all process. Facebook
            delivers the full interactive experience directly in the feed.
            Instagram requires a preview-based strategy with links to the full
            tour. YouTube works for 360 video content. Google Business Profile
            offers the most direct commercial benefit for local businesses.
          </p>
          <p>
            Plan your distribution strategy for each platform before shooting
            and you'll maximise the reach of your 360 content across every
            channel your audience uses.
          </p>
        </section>
        <section>
          <h2>Continue Reading</h2>
          <ul>
          <li><a href="/blog/en/what-is-a-360-photo">What Is a 360 Photo?</a></li>
          <li><a href="/blog/en/how-to-take-360-photos-smartphone">Take 360 Photos with Your Phone</a></li>
          <li><a href="/blog/en/embed-360-virtual-tour-website">Embed 360 Tours on Your Website</a></li>
          <li><a href="/blog/en/360-virtual-tour-image-size">Best Image Size for Sharing</a></li>
          <li><a href="/blog/en/virtual-tour-seo-guide">Virtual Tour SEO Guide</a></li>
          <li><a href="/blog/en/free-virtual-tour-creator">Complete 360 Virtual Tour Guide</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "share-360-photo-social-media",
);

useBlogSeo({
  title: "Share 360 Photos on Facebook & Instagram [2026 Guide] | Travvir",
  description:
    "How to share interactive 360 photos on Facebook, Instagram, and other platforms. Covers format requirements, posting steps, and engagement tips.",
  keywords:
    "post 360 photo facebook, instagram 360 photos, share 360 photo social media, facebook 360 photo upload, 360 photo sharing platforms",
  slug: "share-360-photo-social-media",
  image: postData?.image,
  postData,
  lang: "en",
});

</script>
